import cn from 'classnames'
import React, { useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import InsightsChat from 'src/agent/components/InsightsChat/InsightsChat'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useAgentContext } from 'src/context/AgentContext'
import { useTranslate } from 'src/localization/Trns'
import {
  StructuredJsonType,
  TileSpecification,
} from 'src/queries/agent/network/agent.models'
import { useDynamicTileQuery } from 'src/queries/insights/insights.queries'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectIsAIInsightsEnabled } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import style from './InsightsPage.style'
import InsightsTileRenderer from './InsightsTileRenderer/InsightsTileRenderer'
import { MAX_PINNED_INSIGHTS } from './pinnedInsights.storage'
import PinnedInsightTile from './PinnedInsightTile'
import usePinnedInsights from './usePinnedInsights'

const InsightsPage: React.FC = () => {
  const dispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const translate = useTranslate()
  const showAIInsights = useSelector(selectIsAIInsightsEnabled)
  const userId = useSelector(selectUserIdFromState)

  const { getMessages } = useAgentContext()
  const messages = getMessages('insights')

  const {
    pinnedInsights,
    isPinned: isSpecPinned,
    pin,
    unpin,
  } = usePinnedInsights(userId)

  const showFailureToast = useCallback(
    (message: string) => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <span>{message}</span>,
          failureColorScheme: true,
        }),
      )
    },
    [dispatch],
  )

  const handleTogglePin = useCallback(
    (spec: TileSpecification, shouldPin: boolean) => {
      if (shouldPin) {
        const result = pin(spec)
        if (result === 'limit') {
          showFailureToast(
            translate('You can only pin up to ${count} insights.', {
              count: `${MAX_PINNED_INSIGHTS}`,
            }),
          )
        } else if (result === 'error') {
          showFailureToast(
            translate(
              'We could not save your pinned insight. Please try again.',
            ),
          )
        }
      } else {
        unpin(spec)
      }
    },
    [pin, showFailureToast, translate, unpin],
  )

  const latestTileSpec = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i]
      if (
        msg.sender === 'ai' &&
        msg.structuredJson?.type === StructuredJsonType.TILE_DATA
      ) {
        const candidate = msg.structuredJson.data
        if (!isSpecPinned(candidate)) {
          return candidate
        }
      }
    }
    return null
  }, [isSpecPinned, messages])

  const {
    data: tileData,
    isLoading: isTileLoading,
    error: tileError,
  } = useDynamicTileQuery(latestTileSpec)

  // Redirect to not found page if feature flag is disabled
  if (!showAIInsights) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Insights | ${appName}`}</title>
        <meta name="description" content={`Insights page of ${appName}`} />
      </Helmet>
      <LayoutPage
        css={style}
        className={cn('InsightsPage')}
        padding="none"
        fixedHeight
      >
        <div className={'InsightsPage-columns'}>
          <div className={cn('InsightsPage-left')}>
            <InsightsChat chatKey="insights" />
          </div>
          <div className={cn('InsightsPage-right')}>
            <div className={cn('InsightsPage-right-title')}>
              {translate('Insights')}
            </div>
            <div className={cn('InsightsPage-right-content')}>
              {latestTileSpec && (
                <InsightsTileRenderer
                  tileSpec={latestTileSpec}
                  tileData={tileData}
                  isLoading={isTileLoading}
                  error={tileError}
                  onTogglePin={(shouldPin) =>
                    handleTogglePin(latestTileSpec, shouldPin)
                  }
                  // debug
                />
              )}
              {pinnedInsights.length > 0 && (
                <div className={cn('InsightsPage-right-pinned')}>
                  <div className={cn('InsightsPage-right-pinned-title')}>
                    {translate('Pinned insights')}
                  </div>
                  {pinnedInsights.map((pinnedInsight) => (
                    <PinnedInsightTile
                      key={pinnedInsight.key}
                      pinnedInsight={pinnedInsight}
                      onTogglePin={handleTogglePin}
                    />
                  ))}
                </div>
              )}
              {!latestTileSpec && pinnedInsights.length === 0 && (
                <div className={cn('InsightsPage-right-empty')}>
                  {translate(
                    'Insights will appear here as you chat with the AI assistant.',
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </LayoutPage>
    </>
  )
}

export default InsightsPage
